import React from "react";
import {
  List,
  Datagrid,
  TextField,
  DateField,
  Edit,
  Create,
  SimpleForm,
  TextInput,
  SelectInput,
  ArrayInput,
  SimpleFormIterator,
  FormDataConsumer,
  useNotify,
  useRefresh,
  useRecordContext,
  Button,
} from "react-admin";
import SendIcon from "@mui/icons-material/Send";
import VisibilityIcon from "@mui/icons-material/Visibility";

const targetChoices = [
  { id: "all", name: "An alle Abonnenten" },
  { id: "selected", name: "An ausgewählte Empfänger" },
];

const csvFromList = (value: unknown) => {
  if (Array.isArray(value)) {
    return value.join(", ");
  }
  return value ?? "";
};

const listFromCsv = (value: unknown) => {
  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return value ?? [];
};

const userIdListFromCsv = (value: unknown) => {
  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item) => (item.match(/^\\d+$/) ? Number(item) : item))
      .filter((item) => typeof item === "number");
  }
  return value ?? [];
};

const SendCampaignButton = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();

  if (!record) return null;

  const handleSend = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`/api/newsletter/campaigns/${record.id}/send`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const data = await response.json();
        notify(data.detail || "Versand fehlgeschlagen", { type: "error" });
        return;
      }
      notify("Kampagne wurde in die Queue gestellt", { type: "info" });
      refresh();
    } catch (error) {
      notify("Versand fehlgeschlagen", { type: "error" });
    }
  };

  return (
    <Button
      label="Send Campaign"
      onClick={handleSend}
      startIcon={<SendIcon />}
      disabled={record.status !== "draft"}
    />
  );
};

const PreviewCampaignButton = () => {
  const record = useRecordContext();
  const notify = useNotify();

  if (!record) return null;

  const handlePreview = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`/api/newsletter/campaigns/${record.id}/preview`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const data = await response.json();
        notify(data.detail || "Preview fehlgeschlagen", { type: "error" });
        return;
      }
      const html = await response.text();
      const previewWindow = window.open("", "_blank");
      if (!previewWindow) {
        notify("Popup blockiert", { type: "warning" });
        return;
      }
      previewWindow.document.open();
      previewWindow.document.write(html);
      previewWindow.document.close();
    } catch (error) {
      notify("Preview fehlgeschlagen", { type: "error" });
    }
  };

  return (
    <Button
      label="Preview Mail"
      onClick={handlePreview}
      startIcon={<VisibilityIcon />}
    />
  );
};

export const NewsletterCampaignList = () => (
  <List>
    <Datagrid rowClick="edit">
      <TextField source="name" />
      <TextField source="subject" />
      <TextField source="target_type" />
      <TextField source="status" />
      <DateField source="sent_at" showTime />
    </Datagrid>
  </List>
);

const CampaignFormFields = () => (
  <>
    <TextInput source="name" fullWidth />
    <TextInput source="subject" fullWidth />
    <TextInput source="from_email" fullWidth />
    <SelectInput source="target_type" choices={targetChoices} fullWidth defaultValue="all" />
    <FormDataConsumer>
      {({ formData }) =>
        formData.target_type === "selected" ? (
          <>
            <TextInput
              source="recipient_emails"
              fullWidth
              multiline
              minRows={2}
              label="Empfaenger E-Mails (kommagetrennt)"
              format={csvFromList}
              parse={listFromCsv}
            />
            <TextInput
              source="recipient_user_ids"
              fullWidth
              multiline
              minRows={2}
              label="Empfaenger User-IDs (kommagetrennt)"
              format={csvFromList}
              parse={userIdListFromCsv}
            />
          </>
        ) : null
      }
    </FormDataConsumer>
    <TextInput source="body_html" fullWidth multiline minRows={6} />
    <TextInput source="body_text" fullWidth multiline minRows={4} />
    <TextInput source="status" disabled />
    <TextInput source="sent_at" disabled />
  </>
);

export const NewsletterCampaignCreate = () => (
  <Create>
    <SimpleForm sx={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}>
      <CampaignFormFields />
    </SimpleForm>
  </Create>
);

export const NewsletterCampaignEdit = () => (
  <Edit>
    <SimpleForm sx={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}>
      <CampaignFormFields />
      <PreviewCampaignButton />
      <SendCampaignButton />
    </SimpleForm>
  </Edit>
);
